import { ChevronLeft, Refresh } from "@mui/icons-material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, Button, Container, Stack, useTheme } from "@mui/material";
import {
  Checkmark20Px,
  ConfirmationModal,
  useToast,
  formatDateDayjs
} from "@qubit/autoparts";
import dayjs from "dayjs";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import { getUserClientId } from "~/api/usersTypes/auth0Profile";
import { useAppDispatch, useAppSelector } from "~/app/store";

import { ProductCard } from "~/components/productCard/ProductCardRootWithSideRibbon";
import useFlag from "~/config/flags";
import MultiPort from "~/features/autostoreBin/MultiPort";
import FlagBinModal from "~/features/autostorePutaway/modals/FlagBinModal";
import InventoryHoldModal from "~/features/autostorePutaway/modals/InventoryHoldModal";
import { setBinsFlags } from "~/features/cycleCounts/cycleCounts.slice";
import useClickCooldown from "~/hooks/useClickCooldown";
import { useDevCheats } from "~/hooks/useDevCheats";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { useInactivityResetTimer } from "~/hooks/useInactivityResetTimer";
import { useNavbar } from "~/hooks/useNavbar";
import usePortStatus from "~/hooks/usePortStatus";
import { useShouldListenToGridEvents } from "~/hooks/useShouldListenToGridEvents";

import { runAsyncSafely } from "~/lib/helpers";
import {
  formatCycleCountProductMixpanelRowFromVariant,
  mixpanelTrack,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { QUANTITY_MAX_VALUE } from "~/lib/shared";
import usePromiseInterval from "~/lib/usePromiseIntervalEffect";
import {
  fetchPortStatus,
  closeBin,
  binNotAtPort,
  closeWorkstation,
  getNextBin,
  setNoMoreBins
} from "~/redux/actions/autostore";
import {
  clearSelectedCycleCounts,
  removeCountingTask
} from "~/redux/actions/cycleCounts";
import { clearSelectedVariant } from "~/redux/actions/inventory";
import { StoreState } from "~/redux/reducers";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectIsShortSideBin } from "~/redux/selectors/workstationsSelectors";
import { useLazyGetBinQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { usePostCompleteCountingTaskMutation } from "~/redux/warehouse/cycleCountsOverhaul.hooks";
import { useVariantsByIdQuery } from "~/redux/warehouse/variant.hooks";
import { InventoryDto } from "~/types/api";

import CountingTaskInventoryCard from "./CountingTaskInventoryCard";
import { DEFAULT_REASON_CODE, setReasonCode } from "./cycleCountsNew.slice";

const mapStateToProps = (state: StoreState) => ({
  clientId: getUserClientId(state.login.profile),
  selectedAutostoreGridId: state.workstations.siteWorkstation?.autostoreGridId,
  selectedPortId: state.workstations.sitePortId,
  usersFulfillmentCenter: state.store.usersFulfillmentCenter,
  thisWorkstationId: state.workstations.siteWorkstation?.id,
  selectedCycleCounts: state.cycleCounts.selectedCycleCounts,
  portStateByPort: state.autostore.portStateByPort,
  /** response from nextEmptyBin call */
  nextEmptyBinByPort: state.autostore.nextEmptyBinByPort,
  siteWorkstation: state.workstations.siteWorkstation,
  siteAllPortIds: state.workstations.siteAllPortIds,
  nextBinInventoryByPort: state.autostore.nextBinInventoryByPort,
  noMoreBins: state.autostore.noMoreBins
});

const connector = connect(mapStateToProps, {
  fetchPortStatus,
  binNotAtPort,
  closeBin,
  closeWorkstation,
  getNextBin,
  clearSelectedVariant,
  setNoMoreBins,
  removeCountingTask
});

type PropsFromRedux = ConnectedProps<typeof connector>;
export type CycleCountProps = PropsFromRedux;

export const CycleCountTask = (props: CycleCountProps) => {
  const {
    selectedAutostoreGridId,
    selectedPortId,
    usersFulfillmentCenter,
    portStateByPort,
    nextEmptyBinByPort,
    siteWorkstation,
    thisWorkstationId,
    siteAllPortIds,
    nextBinInventoryByPort,
    selectedCycleCounts,
    noMoreBins
  } = props;

  const binFlaggingEnabled = usersFulfillmentCenter?.binFlaggingEnabled;
  const nextBinInventoryByPortArray = Object.values(nextBinInventoryByPort);
  const nextBinInventoryPortCount = nextBinInventoryByPortArray.length;
  // hooks
  const trackedPageName = useMixpanelPageName();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useFcNavigate();
  const { successToast, errorToast, infoToast } = useToast();
  const { enableColorChanges } = useFlag();
  const { palette } = useTheme();
  const shouldListenToGridEvents = useShouldListenToGridEvents();
  const isInventoryDateFieldDirty = useAppSelector(
    (state) => state.cycleCountsSlice.isInventoryDateFieldDirty
  );
  const { inv_inventoryDateLabel } = useAppSelector(selectServerConfig);
  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  const reasonCode = useAppSelector(
    (state) => state.cycleCountsNewSlice.reasonCode
  );
  const isQuantityDirty = useAppSelector(
    (state) => state.cycleCountsNewSlice.isQuantityDirty
  );

  // local state
  const [isInventoryHoldModalOpen, setIsInventoryHoldModalOpen] =
    useState(false);
  const [isFlagBinModalOpen, setIsFlagBinModalOpen] = useState(false);
  const [selectedInventoryRecord, setSelectedInventoryRecord] =
    useState<InventoryDto | null>(null);
  const [isNavigateAwayModalOpen, setIsNavigateAwayModalOpen] = useState(false);
  // bin fetching state
  const [portPollingActive, setPortPollingActive] = useState<boolean>(false);
  const [binAtPortSeconds, setBinAtPortSeconds] = useState(0);

  const currentSelectedBin = nextBinInventoryByPortArray.find((bin) =>
    bin.nextBinInventory.some(
      (inv) => inv.inventoryId === selectedInventoryRecord?.inventoryId
    )
  );
  const currentSelectedPortId = currentSelectedBin?.portId;

  const { areAllPortsReady, areSomePortsReady } = usePortStatus(
    portStateByPort,
    siteAllPortIds,
    currentSelectedPortId
  );

  const isSelectedBinReady =
    currentSelectedPortId &&
    portStateByPort[currentSelectedPortId]?.getPortResponse.isReady;

  // inventory adjustment state hooks
  const [changedQuantity, setChangedQuantity] = useState<number | undefined>();
  const [inventoryDate, setInventoryDate] = useState<string | null>(null);
  const [isConfirmButtonDisabled, setIsConfirmButtonDisabled] =
    useState<boolean>(true);
  const {
    isCooldownActive: isConfirmCooldownActive,
    startCooldown: startConfirmCooldown
  } = useClickCooldown();

  // query hooks
  const inventoryIds = useMemo(
    () =>
      selectedCycleCounts?.flatMap((cycleCount) => cycleCount.countingTasks) ??
      [],
    [selectedCycleCounts]
  );
  const { data: countingTaskVariantData } = useVariantsByIdQuery({
    variantIds: selectedInventoryRecord ? selectedInventoryRecord.variantId : []
  });
  const [completeCountingTaskPost, { isLoading: isConfirming }] =
    usePostCompleteCountingTaskMutation();
  const [fetchBinInfo] = useLazyGetBinQuery();

  const navigateToCycleCountPageCallback = useCallback(() => {
    dispatch(clearSelectedCycleCounts());
    navigate({
      pathname: `/autostore-cyclecountv2`
    });
  }, [navigate, dispatch]);

  // This function is in the dependency array of the 'inactivityModalCloseCallback' function
  // So we need to wrap it with useCallback to avoid endless re-renders
  const resetPage = useCallback(() => {
    setPortPollingActive(false);
    if (selectedAutostoreGridId && siteAllPortIds) {
      void dispatch(closeWorkstation());
    }
    navigateToCycleCountPageCallback();
    dispatch(setNoMoreBins(false));
  }, [
    selectedAutostoreGridId,
    navigateToCycleCountPageCallback,
    setPortPollingActive,
    dispatch,
    siteAllPortIds
  ]);

  useInactivityResetTimer({
    onInactivityModalClose: resetPage
  });

  // Maps product within bin to product on counting-task table
  const handleBinClickCallback = useCallback(
    (
      binId?: number,
      selectionSource: "user" | "auto" = "user",
      inventoryId?: Guid
    ) => {
      if (binId) {
        if (
          !(selectedInventoryRecord?.bin.autostoreBin?.autostoreBinId === binId)
        ) {
          setChangedQuantity(undefined);
          setInventoryDate(null);
          dispatch(setReasonCode(DEFAULT_REASON_CODE));
        }

        // Return inventory record which matches on the inventoryId within the selected binId
        const selectedInventoryByBin = nextBinInventoryByPortArray
          .flatMap((inv) => inv.nextBinInventory)
          .filter(
            (binInv) =>
              binInv.bin.autostoreBin?.autostoreBinId === binId &&
              inventoryIds?.includes(binInv.inventoryId)
          );
        // if we know the inventoryId (user clicks specific bin), use it. Otherwise default to first inventoryDto
        const userSelectedInventory = inventoryId
          ? selectedInventoryByBin.find(
              (inv) => inv.inventoryId === inventoryId
            )
          : selectedInventoryByBin[0];
        if (userSelectedInventory) {
          const newBinId =
            userSelectedInventory.bin.autostoreBin?.autostoreBinId;
          if (newBinId !== undefined) {
            const prevBinId =
              selectedInventoryRecord?.bin.autostoreBin?.autostoreBinId;
            if (newBinId !== prevBinId && selectionSource === "user") {
              mixpanelTrack({
                trackedPageName,
                type: "Select Option",
                label: "Bin selection changed"
              });
            }
          }
          setIsConfirmButtonDisabled(false);
          setSelectedInventoryRecord(userSelectedInventory);
        }
      }
    },
    [
      inventoryIds,
      nextBinInventoryByPortArray,
      selectedInventoryRecord?.bin.autostoreBin?.autostoreBinId,
      dispatch,
      trackedPageName
    ]
  );

  const handleClickFlagBin = () => {
    if (!isFlagBinModalOpen) {
      mixpanelTrack({
        trackedPageName,
        type: "Button Click",
        label: "Bin problem"
      });
    }
    setIsFlagBinModalOpen((prev) => !prev);
    //get all the flags for the current bins and show them above bins on the flagBinModal
    siteAllPortIds?.map((portId) => {
      const binId = portStateByPort[portId].getPortResponse.selectedBin;
      if (binId && selectedAutostoreGridId) {
        void fetchBinInfo({
          autostoreGridId: selectedAutostoreGridId,
          binNumber: binId
        })
          .unwrap()
          .then((binInfo) => {
            dispatch(setBinsFlags({ binId: binId, flags: binInfo.flags }));
          });
      }
    });
  };

  // Port polling: if log publisher enabled, poll port and log publisher state
  const waitInterval = shouldListenToGridEvents ? 7 : 1;
  usePromiseInterval(
    async () => {
      setBinAtPortSeconds((binAtPortSecondsState) => binAtPortSecondsState + 1);
      if (binAtPortSeconds > waitInterval && siteWorkstation) {
        await Promise.all(
          siteWorkstation.ports.map((port) => {
            const portStateResponse =
              portStateByPort[port.portId]?.getPortResponse;
            if (
              !portStateResponse?.isReady &&
              portStateResponse?.selectedBin !== 0
            ) {
              return props.fetchPortStatus({ portId: port.portId });
            }
            return Promise.resolve();
          })
        );
      }
    },
    1000,
    !areAllPortsReady
  );

  const onClickIncreaseQuantity = (previousQty: number) => {
    const newQuantity = (previousQty ?? 0) + 1;

    if (newQuantity > QUANTITY_MAX_VALUE) {
      return;
    }

    setChangedQuantity(newQuantity);
  };

  const onClickDecreaseQuantity = (previousQty: number) => {
    const newQuantity = Math.max((previousQty ?? 1) - 1, 0);
    if (newQuantity >= 0) {
      setChangedQuantity(newQuantity);
    } else {
      errorToast("Quantity can't be negative");
    }
  };

  const handleGetNextBin = async (portId: number) => {
    await runAsyncSafely(async () => {
      const portState = portStateByPort[portId];
      if (portState?.getPortResponse.selectedBin) {
        await props.closeBin({
          binId: portState.getPortResponse.selectedBin,
          taskId: portState.getPortResponse.selectedTask,
          portId
        });
      }
      await props.getNextBin({ portId, shouldSoftFail: true });

      if (!shouldListenToGridEvents) {
        setPortPollingActive(true);
      } else {
        props.binNotAtPort();
      }

      // Get port status again in order to update portState's ready to be false while calling 'next bin'
      await props.fetchPortStatus({ portId });
    });
  };

  const selectedCountingTask =
    selectedInventoryRecord &&
    selectedCycleCounts?.find((cc) =>
      cc.countingTasks.includes(selectedInventoryRecord.inventoryId)
    );
  const isSelectedInventoryTaskStale =
    !!selectedInventoryRecord &&
    !inventoryIds.includes(selectedInventoryRecord.inventoryId);
  const countingTaskNoLongerExistsMatch = "Counting task no longer exists";

  const handleCompleteCountingTask = async () => {
    if (
      !selectedInventoryRecord ||
      !selectedInventoryRecord.count.value ||
      !selectedCountingTask ||
      !selectedAutostoreGridId ||
      !currentSelectedPortId ||
      !thisWorkstationId
    ) {
      errorToast("Can't complete task, no grid information found");
      return;
    }

    if (isConfirming || isConfirmCooldownActive) {
      return;
    }

    const getDates = () => {
      const { manufactureDate, expiration } = selectedInventoryRecord;
      let actualManufactureDate;
      let actualExpirationDate;
      if (inv_inventoryDateLabel === "manufacture") {
        const invDate = inventoryDate ?? manufactureDate;
        if (invDate) {
          actualManufactureDate = formatDateDayjs(invDate);
        }
      } else if (inv_inventoryDateLabel === "expiration") {
        const invDate = inventoryDate ?? expiration;
        if (invDate) {
          actualExpirationDate = formatDateDayjs(invDate);
        }
      }
      return { actualManufactureDate, actualExpirationDate };
    };
    const { actualManufactureDate, actualExpirationDate } = getDates();

    if (
      countingTaskVariantData?.at(0)?.isExpirationRequired &&
      !actualExpirationDate
    ) {
      errorToast("Expiration Date can't be empty or invalid");
      return;
    }

    mixpanelTrack({
      trackedPageName,
      type: "Button Click",
      label: "Confirm",
      eventProperties: {
        "Cycle Counts Product Data":
          formatCycleCountProductMixpanelRowFromVariant(
            countingTaskVariantData?.[0]
          )
      }
    });

    startConfirmCooldown();

    try {
      await completeCountingTaskPost({
        cycleCountId: selectedCountingTask.cycleCountId,
        inventoryId: selectedInventoryRecord.inventoryId,
        actualCount: {
          value: changedQuantity ?? selectedInventoryRecord.count.value,
          units: selectedInventoryRecord.count.units
        },
        actualExpiration: actualExpirationDate,
        actualManufactureDate: actualManufactureDate,
        autostoreGridId: selectedAutostoreGridId,
        autostorePortId: currentSelectedPortId,
        workstationId: thisWorkstationId,
        reasonCode
      }).unwrap();
      props.removeCountingTask(selectedInventoryRecord.inventoryId);
    } catch (err: unknown) {
      const errorMessage = getMessageFromRtkError(err);
      if (errorMessage.includes(countingTaskNoLongerExistsMatch)) {
        props.removeCountingTask(selectedInventoryRecord.inventoryId);
        infoToast(t("counting task no longer exists"));
        props.clearSelectedVariant();
        setIsConfirmButtonDisabled(true);
        await handleGetNextBin(currentSelectedPortId);
        setSelectedInventoryRecord(null);
        return;
      }
      errorToast(errorMessage);
      return;
    }

    successToast(t("counting task completed successfully"));
    props.clearSelectedVariant();
    setIsConfirmButtonDisabled(true);
    await handleGetNextBin(currentSelectedPortId);
    setSelectedInventoryRecord(null);
  };

  // Starts our Counting Tasks
  useEffect(() => {
    const cycleCountIds = selectedCycleCounts?.map((cc) => cc.cycleCountId);
    if (cycleCountIds?.length && siteAllPortIds) {
      const getAllNextBins = async () => {
        for (const portId of siteAllPortIds) {
          await handleGetNextBin(portId);
        }
      };
      void getAllNextBins();
    }
    // this is intentional, we only want this to run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop polling when bin arrives
  useEffect(() => {
    if (!shouldListenToGridEvents && areAllPortsReady) {
      setPortPollingActive(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portStateByPort]);

  // Auto select bin
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (selectedInventoryRecord) {
      const currentBin = nextBinInventoryByPortArray.find((binData) =>
        binData.nextBinInventory.some(
          (inv) => inv.inventoryId === selectedInventoryRecord.inventoryId
        )
      );
      if (
        currentBin?.portId &&
        portStateByPort[currentBin.portId]?.getPortResponse.isReady
      ) {
        return;
      }
    }

    const binToSelect = nextBinInventoryByPortArray.find((binData) => {
      const hasInventory = binData.nextBinInventory.some((inv) =>
        inventoryIds?.includes(inv.inventoryId)
      );
      const isReady = portStateByPort[binData.portId]?.getPortResponse.isReady;
      return hasInventory && isReady;
    });

    const binIdToSelect = binToSelect?.nextBinResponse?.openBinResponse.binId;
    const currentBinId =
      selectedInventoryRecord?.bin.autostoreBin?.autostoreBinId;

    if (
      binIdToSelect &&
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      (!selectedInventoryRecord || binIdToSelect !== currentBinId)
    ) {
      handleBinClickCallback(binIdToSelect, "auto");
    }
  }, [
    selectedInventoryRecord,
    nextBinInventoryByPortArray,
    inventoryIds,
    portStateByPort,
    handleBinClickCallback
  ]);

  // On component dismounting, close the port & cleanup the workstation
  useEffect(() => {
    return () => {
      if (selectedAutostoreGridId && siteAllPortIds) {
        // close workstation
        void runAsyncSafely(async () => {
          await dispatch(closeWorkstation());
          dispatch(binNotAtPort());
        });
      }
    };
  }, [selectedAutostoreGridId, siteAllPortIds, dispatch]);

  useDevCheats({ isPortPolling: portPollingActive, showAutostoreStatus: true });
  const { setMenuItems } = useNavbar({
    viewTitle: "nav.viewname.cycle counts"
  });

  useEffect(() => {
    setMenuItems(
      selectedAutostoreGridId && selectedPortId
        ? [
            {
              textContent: t("close workstation"),
              actionCb: resetPage
            }
          ]
        : []
    );
  }, [resetPage, selectedAutostoreGridId, selectedPortId, setMenuItems, t]);

  useEffect(() => {
    if (noMoreBins && nextBinInventoryPortCount === 0) {
      successToast(t("all counting tasks completed successfully"));
      setTimeout(() => {
        // Fire reset page once summariesToDisplay are 0 on page 1
        // The delay allows the incomplete counting tasks projection to be updated
        // so the user does not see the completed task on the cycle count page
        resetPage();
      }, 1200);
    }
  }, [noMoreBins, resetPage, successToast, nextBinInventoryPortCount, t]);

  const getIsInventoryDateValid = (): boolean => {
    const formattedInventoryDate =
      !dayjs.isDayjs(inventoryDate) && inventoryDate !== null
        ? dayjs(inventoryDate)
        : inventoryDate;
    const isDateValid =
      isInventoryDateFieldDirty && formattedInventoryDate
        ? formattedInventoryDate.isValid()
        : true;
    if (!isDateValid !== isConfirmButtonDisabled) {
      setIsConfirmButtonDisabled(!isDateValid);
    }
    return isDateValid;
  };

  const selectedCompartment = selectedInventoryRecord?.bin
    .autostoreCompartmentNumber
    ? selectedInventoryRecord.bin.autostoreCompartmentNumber - 1
    : null;

  const backButton = (
    <Button
      onClick={() => setIsNavigateAwayModalOpen(true)}
      startIcon={enableColorChanges ? <ChevronLeft /> : <Refresh />}
      variant={enableColorChanges ? "text" : "subtle"}
      sx={
        enableColorChanges
          ? {
              alignSelf: "flex-start",
              minWidth: 0,
              px: 0,
              "&&": {
                backgroundColor: "transparent",
                color: palette.autostoreDesignSystem.text.onStandard.primary,
                border: "none",
                "@media (hover: hover)": {
                  "&:hover": {
                    backgroundColor:
                      palette.autostoreDesignSystem.surface.interactive.base
                        .hover
                  }
                }
              }
            }
          : { mt: 2 }
      }
    >
      {t("back")}
    </Button>
  );

  return (
    <Container
      maxWidth="xl"
      sx={{ px: 2, ...(enableColorChanges ? { position: "relative" } : {}) }}
    >
      {enableColorChanges && (
        <Box position="absolute" top={0} left={16} zIndex={1}>
          {backButton}
        </Box>
      )}
      <Stack direction="column" spacing={1}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          position="relative"
          style={{
            marginTop: "8px",
            height: "300px"
          }}
        >
          <Box position="relative" width="200px">
            {!enableColorChanges && (
              <Box position="absolute" right="16px">
                {backButton}
              </Box>
            )}
          </Box>
          <MultiPort
            pickQuantity={0}
            nextBinInventoryByPort={nextBinInventoryByPort}
            nextEmptyBinByPort={nextEmptyBinByPort}
            workstation={siteWorkstation}
            currentSelectedBinId={
              currentSelectedBin?.nextBinResponse?.openBinResponse.binId
            }
            selectedSummariesAtPort={inventoryIds}
            selectedCompartment={selectedCompartment ?? undefined}
            onBinClick={(_, binId) => handleBinClickCallback(binId)}
            isShortSideBin={isShortSideBin}
          />
          <Box position="relative" width="200px">
            <Box position="absolute" left="16px" top="calc(50% - 15px)">
              {binFlaggingEnabled && (
                <Button
                  variant="subtle"
                  onClick={handleClickFlagBin}
                  disabled={!areSomePortsReady}
                >
                  {t("bin problem")}
                </Button>
              )}
            </Box>
          </Box>
        </Stack>
        <Stack direction="column" width="500px" spacing={2} alignSelf="center">
          {selectedCycleCounts?.length &&
          countingTaskVariantData?.length &&
          !!selectedInventoryRecord ? (
            <CountingTaskInventoryCard
              selectedVariant={countingTaskVariantData[0]}
              inv_inventoryDateLabel={inv_inventoryDateLabel}
              inventoryDate={inventoryDate}
              setInventoryDate={setInventoryDate}
              selectedInventoryRecord={selectedInventoryRecord}
              changedQuantity={changedQuantity}
              onClickIncreaseQuantity={onClickIncreaseQuantity}
              onClickDecreaseQuantity={onClickDecreaseQuantity}
              handleChangeQuantity={(count) => setChangedQuantity(count)}
              isDateValid={getIsInventoryDateValid()}
              isCycleCountBlind={selectedCountingTask?.isBlind ?? false}
            />
          ) : (
            <ProductCard.Loading height="540px" disableGutters />
          )}
          <Stack direction="row" spacing={2}>
            <Button
              variant="subtle"
              size="large"
              startIcon={<ErrorOutlineIcon />}
              onClick={() => {
                if (!isInventoryHoldModalOpen) {
                  mixpanelTrack({
                    trackedPageName,
                    type: "Button Click",
                    label: "Inventory hold",
                    eventProperties: {
                      "Open counting tasks remaining": inventoryIds.length
                    }
                  });
                }
                setIsInventoryHoldModalOpen((prev) => !prev);
              }}
              disabled={!isSelectedBinReady}
              sx={{ px: 6, width: "100%", fontWeight: "normal" }}
            >
              {t("inventory hold")}
            </Button>
            <Button
              size="large"
              sx={{ width: "100%", fontWeight: "normal" }}
              startIcon={<Checkmark20Px style={{ fill: "white" }} />}
              onClick={handleCompleteCountingTask}
              disabled={
                isConfirming ||
                isConfirmCooldownActive ||
                isSelectedInventoryTaskStale ||
                isConfirmButtonDisabled ||
                !isSelectedBinReady ||
                isNaN(changedQuantity ?? 0) ||
                !(changedQuantity || selectedInventoryRecord?.count?.value) ||
                (changedQuantity === undefined &&
                  selectedCountingTask?.isBlind) || // if blind, user must enter a count before confirming
                ((isQuantityDirty || isInventoryDateFieldDirty) && !reasonCode)
              }
            >
              {t("confirm")}
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <InventoryHoldModal
        isOpen={isInventoryHoldModalOpen}
        onClose={() => {
          setIsInventoryHoldModalOpen(false);
        }}
        nextEmptyBinByPort={nextEmptyBinByPort}
        nextBinInventoryByPort={nextBinInventoryByPort}
        currentSelectedBin={currentSelectedBin?.nextBinResponse ?? null}
        currentSelectedCompartment={selectedCompartment}
      />
      <FlagBinModal
        isOpen={isFlagBinModalOpen}
        onClose={() => setIsFlagBinModalOpen(false)}
        nextBinInventoryByPort={nextBinInventoryByPort}
        nextEmptyBinByPort={nextEmptyBinByPort}
        portStateByPort={portStateByPort}
        workstation={siteWorkstation}
        shouldCloseBinUponSubmit={false}
        selectedBin={currentSelectedBin?.nextBinResponse?.openBinResponse.binId}
        onSuccess={() => {
          successToast(t("flag set successfully"));
        }}
      />
      <ConfirmationModal
        isOpen={isNavigateAwayModalOpen}
        labelledById="cycle-count-navigate-away-modal-title"
        modalTitle={t("are you sure you want to navigate out of the page")}
        modalText=""
        confirmCb={() => {
          const outstanding = inventoryIds.length;
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: "Return to Cycle Counts",
            eventProperties: {
              "Open counting tasks remaining": outstanding,
              "Back before all tasks completed": outstanding > 0
            }
          });
          setIsNavigateAwayModalOpen(false);
          resetPage();
        }}
        closeCb={() => setIsNavigateAwayModalOpen(false)}
      />
    </Container>
  );
};

export default connector(CycleCountTask);
